import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import type { Movie } from "@/types/content";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const tabs = [
  { key: "all", label: "全部" },
  { key: "tv", label: "电视剧" },
  { key: "movie", label: "电影" },
  { key: "anime", label: "动漫" },
  { key: "news", label: "资讯" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState("all");

  usePageMeta(
    q ? `搜索“${q}” - 蜜月TV` : "搜索 - 蜜月TV",
    "在蜜月TV搜索影视剧、电影、动漫与影视资讯，快速找到你想看的内容。",
  );

  const matchedMovies = useMemo(() => {
    if (!q.trim()) return [] as Movie[];
    const k = q.trim().toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(k) ||
        m.genres.some((g) => g.includes(q.trim())) ||
        m.director.includes(q.trim()) ||
        m.cast.some((c) => c.includes(q.trim())),
    );
  }, [q]);

  const matchedNews = useMemo(() => {
    if (!q.trim()) return [];
    return newsList.filter(
      (n) => n.title.includes(q.trim()) || n.summary.includes(q.trim()),
    );
  }, [q]);

  const showMovies = tab === "all" || tab === "tv" || tab === "movie" || tab === "anime";
  const showNews = tab === "all" || tab === "news";

  const filteredMovies = showMovies
    ? tab === "all"
      ? matchedMovies
      : matchedMovies.filter((m) => m.type === tab)
    : [];
  const filteredNews = showNews ? matchedNews : [];

  const total = filteredMovies.length + filteredNews.length;
  const hasQuery = q.trim().length > 0;

  const suggestions = useMemo(
    () => [...movies].sort((a, b) => b.rating - a.rating).slice(0, 6),
    [],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setParams(value ? { q: value } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-4">
        <h1 className="text-2xl md:text-3xl font-black text-foreground-950">搜索</h1>
        <p className="text-sm text-foreground-500 mt-2">查找影视剧、电影、动漫与影视资讯。</p>
      </header>

      <form onSubmit={handleSubmit} className="relative mt-5 max-w-2xl">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="输入影视名称、类型或导演关键词"
          className="w-full text-sm pl-11 pr-28 py-3.5 rounded-full bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <button
          type="submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {hasQuery && (
        <p className="text-sm text-foreground-500 mt-5">
          关键词“<span className="font-medium text-foreground-800">{q}</span>”共找到{" "}
          <span className="font-bold text-primary-700">{total}</span> 条结果
        </p>
      )}

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              tab === item.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {!hasQuery ? (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg bg-background-50 border border-dashed border-background-300">
            <i className="ri-search-eye-line text-5xl text-foreground-300" />
            <h2 className="text-base font-bold text-foreground-800 mt-4">输入关键词开始搜索</h2>
            <p className="text-sm text-foreground-500 mt-2">试试搜索剧名、类型，例如“科幻”“悬疑”。</p>
          </div>
        ) : total === 0 ? (
          <div>
            <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg bg-background-50 border border-dashed border-background-300">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
              <h2 className="text-base font-bold text-foreground-800 mt-4">没有找到相关内容</h2>
              <p className="text-sm text-foreground-500 mt-2">换个关键词试试，或看看下面的热门推荐。</p>
            </div>
            <section className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950 mb-4">热门推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {suggestions.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <>
            {filteredMovies.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-primary-500" /> 影视结果
                  <span className="text-sm font-normal text-foreground-400">（{filteredMovies.length}）</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </section>
            )}

            {filteredNews.length > 0 && (
              <section className={filteredMovies.length > 0 ? "mt-10" : ""}>
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500" /> 资讯结果
                  <span className="text-sm font-normal text-foreground-400">（{filteredNews.length}）</span>
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="shrink-0 w-28 h-20 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 配图`}
                          title={`${item.title} - 蜜月TV影视资讯`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </h3>
                        <span className="text-xs text-foreground-400 mt-1.5 block">{item.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}